import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  FilterByDateParams,
  OrdenTrabajo,
  OrdenesTrabajoPagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { useEffect, useRef } from 'react';


const { get, post, put, remove } = erpApi();

const validarFecha = (value:string) =>{
  if(!value) return false;
  if(value.includes("MM") || value.includes("AAAA") || value.includes("DD")){
    return false;
  }

  const date = new Date(value)
  return !isNaN(date.getTime());
}

export const validarFiltrosOrdenTrabajo = (params?:GetOrdenTrabajosParams) => {
  return (
    params?.fecha_instalacion && !validarFecha(params.fecha_instalacion)
  )
}


export enum OrdenTrabajoQueryKeysEnum {
  OrdenTrabajo = 'orden-trabajo',
  OrdenesTrabajo = 'orden-trabajos',
}

export const useFetchOrdenTrabajosInterval = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetOrdenTrabajosParams> = {}) => {
  return useQuery({
    queryKey: [
      OrdenTrabajoQueryKeysEnum.OrdenesTrabajo,
      ...Object.values(params ?? {}),
    ],
    queryFn: () => getOrdenTrabajos(params),
    enabled: enabled,
    refetchInterval: 60000
  });
};

///* tanStack query
export const useFetchOrdenTrabajos = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetOrdenTrabajosParams> = {}) => {
    const setAlert = useUiStore.getState().setAlert;
  // const validacion = params?.fecha_instalacion && !validarFecha(params.fecha_instalacion);

   const shownRef = useRef(false);
  // if(validacion){
  //   setAlert(true, 'Fecha de instalación inválida', AlertSeverityEnum.error);
  // }
  const invalid = validarFiltrosOrdenTrabajo(params);
 useEffect(() => {
    if (invalid && !shownRef.current) {
      setAlert(
        true,
        'Fecha de instalación inválida',
        AlertSeverityEnum.error
      );

      shownRef.current = true;
    }

    if (!invalid) {
      shownRef.current = false;
    }
  }, [invalid, setAlert]);


  return useQuery({
    queryKey: [
      OrdenTrabajoQueryKeysEnum.OrdenesTrabajo,
      ...Object.values(params ?? {}),
    ],
    queryFn: () => getOrdenTrabajos(params),
    enabled: enabled,
    gcTime:1000
  });
};

export const useGetOrdenTrabajo = (id: string, enabled = true) => {
  return useQuery({

    queryKey: [OrdenTrabajoQueryKeysEnum.OrdenTrabajo, id?.toString()],
    queryFn: () => getOrdenTrabajo(id),
    retry: false,
    gcTime:1000,
    enabled: enabled && !!id,
  });
};

export const useCreateOrdenTrabajo = <T>({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: CreateOrdenTrabajoParams<T>) =>
      createOrdenTrabajo(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [OrdenTrabajoQueryKeysEnum.OrdenesTrabajo],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'OrdenTrabajo creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateOrdenTrabajo = <T>({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,

  keysToInvalidate = [],
  enableLoading = true,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: UpdateOrdenTrabajoParams<T>) =>
      updateOrdenTrabajo(params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...keysToInvalidate,
          OrdenTrabajoQueryKeysEnum.OrdenesTrabajo,
        ],
      });
      console.log("variables.data", variables.data);
      const esBorrador = (variables.data as { es_borrador?: boolean })?.es_borrador;

      enableRedirect && navigate && returnUrl && !esBorrador && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'OrdenTrabajo actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      enableLoading && setIsGlobalLoading(false);
    },
  });
};

export const useDeleteOrdenTrabajo = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: deleteOrdenTrabajo,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [OrdenTrabajoQueryKeysEnum.OrdenesTrabajo],
      });
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetOrdenTrabajosParams = Partial<OrdenTrabajo> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
  identificacion?: string;
  fecha_instalacion?: string;

  fecha_creacion_after?:string | null;
  fecha_creacion_before?:string | null;

  fecha_instalacion_after?: string | null;
  fecha_modificacion_after?: string | null;
  
  // scope por tipo de servicio (backend lo mapea a agendamiento__tipo_orden_servicio)
  tipo_orden_servicio?: string;

  // exclude
  estado_orden_trabajo_exclude?: string;
  estado_imagen_actualizacion_exclude?: string;
  tipo_orden_servicio_exclude?: string;
} & FilterByDateParams;
export type CreateOrdenTrabajoParamsForm = Omit<
  OrdenTrabajo,
  'id_orden_trabajo'
>;
export type CreateOrdenTrabajoParams<T> = T;
export type UpdateOrdenTrabajoParams<T> = {
  id: string;
  data: T;
};

export const getOrdenTrabajos = async (params?: GetOrdenTrabajosParams) => {
  const stateParams = { ...params };
  
  
  if(stateParams.fecha_creacion){
     stateParams.fecha_creacion_after = stateParams.fecha_creacion
     delete stateParams.fecha_creacion
  }
  if(stateParams.fecha_modificacion){
     stateParams. fecha_modificacion_after = stateParams.fecha_modificacion
     delete stateParams.fecha_modificacion
  }
  
  if(stateParams.fecha_instalacion){
    if(!validarFecha(stateParams.fecha_instalacion ? stateParams.fecha_instalacion : '')){
         delete stateParams.fecha_instalacion; 
    }else{
      stateParams.fecha_instalacion_after = stateParams.fecha_instalacion;
      delete stateParams.fecha_instalacion; 
    }
    
  }

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;
  
  const queryParams = getUrlParamsFetch(stateParams);

  return get<OrdenesTrabajoPagingRes>(
    `${urlBackend}api/v1/orden-trabajo/?${queryParams}`,
    true
  );
};

export const getOrdenTrabajo = async (id: string) => {
  try {
    return await get<OrdenTrabajo>(
      `${urlBackend}api/v1/orden-trabajo/${id}/`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createOrdenTrabajo = async <T>(
  data: CreateOrdenTrabajoParams<T>
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<OrdenTrabajo>(`${urlBackend}api/v1/orden-trabajo/`, data, true);
};

export const updateOrdenTrabajo = async <T>({
  id,
  data,
}: UpdateOrdenTrabajoParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<OrdenTrabajo>(
    `${urlBackend}api/v1/orden-trabajo/${id}/`,
    data,
    true
  );
};

export const deleteOrdenTrabajo = async (id: string) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  return await remove<OrdenTrabajo>(
    `${urlBackend}api/v1/orden-trabajo/${id}/`,
    true
  );
};